import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {L10nTranslationService} from 'angular-l10n';
import {NEVER, Observable} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {destroy$} from '../destroyable';
import {ModalService} from '../modal';
import {ALERT_COMMON_STRINGS, AlertModalComponent} from '../modal/alert-modal';
import {SystemStatusService} from './system-status.service';

@Component({
    selector: 'system-status-indicator',
    templateUrl: './system-status-indicator.component.html',
    styleUrls: ['./system-status-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SystemStatusIndicatorComponent implements OnInit {
    public isOk: boolean = false;

    public readonly destroy$ = destroy$();

    constructor(
        private systemStatusService: SystemStatusService,
        private changeDetectorRef: ChangeDetectorRef,
        private modalService: ModalService,
        private translationService: L10nTranslationService
    ) {}

    public ngOnInit(): void {
        this.systemStatusService.isOk$.pipe(
            bindWith(this.systemStatusService.keepStatusStreamConnection$),
            takeUntil(this.destroy$),
            tap(isOk => {
                this.isOk = isOk;

                this.changeDetectorRef.detectChanges();
            }),
            switchMap(isOk => isOk
                ? NEVER
                : this.showAlert()
            )
        ).subscribe();
    }

    private showAlert(): Observable<unknown> {
        const getAlertMessage = () => {
            const failsEncodedMsg = this.systemStatusService.getFailsEncodedMsg();

            return [
                this.translationService.translate('components.system-status-indicator.alert_message'),
                ...(
                    failsEncodedMsg
                        ? [
                            this.translationService.translate('components.system-status-indicator.alert_message_codes'),
                            failsEncodedMsg
                        ]
                        : []
                )
            ].join('\n');
        };

        const modalRef = this.modalService.open(AlertModalComponent, {
            destroyWhen: this.destroy$,
            configure:   {
                alertTitle:            'components.system-status-indicator.alert_title',
                alertButtonText:       ALERT_COMMON_STRINGS.buttonConfirm,
                alertMessage:          getAlertMessage(),
                alertMessageTranslate: false
            }
        });

        return this.systemStatusService.updated$.pipe(
            tap(() => {
                const alertMessage = getAlertMessage();

                if (modalRef.componentRef.instance.alertMessage !== alertMessage) {
                    console.warn(alertMessage);

                    modalRef.componentRef.instance.alertMessage = alertMessage;
                    modalRef.componentRef.changeDetectorRef.detectChanges();
                }
            }),
            takeUntil(modalRef.asObservable()),
            takeUntil(this.destroy$)
        );
    }
}
